import {
  Box,
  Pagination,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
  alpha,
  TextField,
  InputAdornment,
  Button
} from '@mui/material';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { format } from 'date-fns';

import EmptyContent from 'src/components/empty-content';
import { useGetCalendarEventsQuery } from 'src/services/calendar/calendar.service';
import Iconify from 'src/components/iconify';
import { width } from '@mui/system';
import CalendarEventCard from '../components/calendar-event-card';
import CalendarListSkeleton from '../components/calendar-list-skeleton';

// ----------------------------------------------------------------------

interface CalendarListViewProps {
  calendarId: string;
}

export default function CalendarListView({ calendarId }: CalendarListViewProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [searchParams, setSearchParams] = useSearchParams();

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(5);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  // Initialize state from URL params
  useEffect(() => {
    const dateParam = searchParams.get('date');
    const searchParam = searchParams.get('search');
    const pageParam = searchParams.get('page');

    if (dateParam) {
      setSelectedDate(new Date(dateParam));
    }

    if (searchParam) {
      setSearchQuery(searchParam);
    }

    if (pageParam) {
      setPage(Number(pageParam));
    }
  }, [searchParams]);

  // Update URL params when filters change
  const updateUrlParams = (newParams: { date?: string | null; search_query?: string | null; page?: number }) => {
    const params = new URLSearchParams(searchParams);

    if (newParams.date !== undefined) {
      if (newParams.date) {
        params.set('date', newParams.date);
      } else {
        params.delete('date');
      }
    }

    if (newParams.search_query !== undefined) {
      if (newParams.search_query) {
        params.set('search', newParams.search_query);
      } else {
        params.delete('search');
      }
    }

    if (newParams.page !== undefined) {
      params.set('page', newParams.page.toString());
    }

    setSearchParams(params);
  };

  const handleDateChange = (date: Date | null) => {
    setSelectedDate(date);
    updateUrlParams({
      date: date ? format(date, 'yyyy-MM-dd') : null
    });
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  const handleSearchSubmit = () => {
    updateUrlParams({ search_query: searchQuery || null });
  };

  const handleClearFilters = () => {
    setSelectedDate(null);
    setSearchQuery('');
    updateUrlParams({ date: null, search_query: null });
  };

  const { data, isLoading, error } = useGetCalendarEventsQuery({
    calendarId,
    page,
    per_page: perPage,
    date: selectedDate ? format(selectedDate, 'dd-MM-yyyy') : undefined,
    search_query: searchQuery || undefined,
  }, {
    skip: !calendarId
  });

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
    updateUrlParams({ page: value });
  };

  if (error) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="error" gutterBottom>
          {t('calendar.error.loading')}
        </Typography>
        <Typography variant="body2">
          {t('calendar.error.try_again')}
        </Typography>
      </Box>
    );
  }

  if (isLoading) {
    return <CalendarListSkeleton />;
  }

  const totalPages = Math.ceil((data?.count || 0) / perPage);
  const events = data?.results || [];

  return (
    <Box sx={{
      p: { xs: 2, md: 3 },
      maxWidth: '100%',
      overflow: 'hidden',
    }}>
      {/* Header with background and improved styling */}
      <Box
        sx={{
          mb: 3,
          p: { xs: 2, md: 3 },
          borderRadius: 2,
          background: `linear-gradient(to right, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.02)})`,
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          boxShadow: `inset 0 0 0 1px ${alpha(theme.palette.primary.main, 0.12)}`,
        }}
      >
        <Box>
          <Typography
            variant="h5"
            sx={{
              mb: 0.5,
              display: 'flex',
              alignItems: 'center',
              color: theme.palette.primary.main,
            }}
          >
            <Iconify icon="mdi:calendar" sx={{ mr: 1, width: 24, height: 24 }} />
            {t('calendar.upcoming_events')}
          </Typography>
          <Typography variant="subtitle2" color="text.secondary">
            {t('calendar.total_events', { count: data?.count || 0 })}
          </Typography>
        </Box>

        {/* Pagination controls at the top for desktop */}
        {events.length > 0 && totalPages > 1 && (
          <Box sx={{ mt: { xs: 2, sm: 0 }, display: { xs: 'none', md: 'block' } }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              color="primary"
              size={isMobile ? 'small' : 'medium'}
              showFirstButton
              showLastButton
            />
          </Box>
        )}
      </Box>

      {/* Filter controls */}
      <Box sx={{
        my: 2,
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        gap: 2,
        alignItems: 'center',
        justifyContent: 'end',
      }}>
        <DatePicker
          label={t('calendar.filter_by_date')}
          value={selectedDate}
          onChange={handleDateChange}
          slotProps={{
            textField: {
              size: 'small',
              sx: { minWidth: 180, width: { xs: '100%', md: 200 } },
            }
          }}
        />

        <TextField
          size="small"
          label={t('common.search')}
          value={searchQuery}
          onChange={handleSearchChange}
          onKeyPress={(e) => e.key === 'Enter' && handleSearchSubmit()}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Iconify
                  icon="eva:search-fill"
                  sx={{ cursor: 'pointer' }}
                  onClick={handleSearchSubmit}
                />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 180, width: { xs: '100%', md: 200 } }}
        />

        {(selectedDate || searchQuery) && (
          <Button
            size="small"
            color="inherit"
            onClick={handleClearFilters}
            sx={{ minWidth: 120, width: { xs: '100%', md: 150 } }}
            startIcon={<Iconify icon="mdi:filter-remove" />}
          >
            {t('common.clear')}
          </Button>
        )}
      </Box>

      {events.length === 0 ? (
        <Box
          sx={{
            p: 4,
            textAlign: 'center',
            border: `1px dashed ${theme.palette.divider}`,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.background.default, 0.8),
            boxShadow: theme.shadows[1],
          }}
        >
          <EmptyContent />
          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            {t('calendar.no_events')}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 500, mx: 'auto' }}>
            {t('calendar.no_events_description')}
          </Typography>
        </Box>
      ) : (
        <Box>
          {/* Grid layout for larger screens, stack for mobile */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr'
              },
              gap: 3,
              mb: 3,
            }}
          >
            {events.map((event) => (
              <CalendarEventCard key={event.id} event={event} />
            ))}
          </Box>

          {/* Pagination at the bottom */}
          {totalPages > 1 && (
            <Box
              sx={{
                display: { xs: 'flex', md: 'none' }, // Hide on desktop as we show it at the top
                justifyContent: 'center',
                mt: 4,
                pb: 2
              }}
            >
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
                size={isMobile ? 'small' : 'medium'}
                siblingCount={isMobile ? 0 : 1}
              />
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}